import React, { useState, useEffect, useCallback } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import classNames from 'classnames';
import SideCartEmpty from './SideCartEmpty';
import PopoverHelp from '../../components/popover-help';
import LoaderButton from '../../components/loaders/LoaderButton';
import { getSessionSettingsStorage } from '../../store/session-storage';
import { useRestaurant } from '../../contexts/restaurant.context';
import {
    hideCartItem,
    removeItemFromCart,
    resetCart,
    showCartItem, updateCartItem
} from '../../store/actions/cart.actions';
import { hideSidebar, sidebars } from '../../store/actions/sidebar.actions';
import { pages, showPage } from '../../store/actions/common.actions';
import { showMenuAlert } from '../../store/actions/menu.actions';
import { showMinimumCheckoutAlert } from '../../store/actions/checkout.actions';
import SelectMenuItemDialog from '../../components/SelectMenuItemDialog';
import MenuAlert from '../../components/MenuAlert';
import { useCart } from '../../contexts/cart.context';
import { CHECKOUT_GIFT_ROUTE, CHECKOUT_ROUTE, HOME_ROUTE } from '../../router/constants';
import { axiosClient } from '../../libs/axios';
import { orderMethods, paymentMethods } from '../../constants/checkout';

function SideCart({history}) {
    const restaurant = useRestaurant();
    const categories = restaurant.menu_categories;
    
    const dispatch = useDispatch();

    const [isGroup, setIsGroup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const selectedItem = useSelector(state => state.cart.selectedItem);
    const activeSidebar = useSelector((state) => state.sidebar.active);
    
    const cart = useSelector(state => state.cart.items);
    const orderMethod = useSelector((state) => state.checkout.orderMethod);
    const paymentMethod = useSelector((state) => state.checkout.paymentMethod);
    const cartGifts = useSelector(state => state.cart.giftItems);

    const {
        subTotal,
        taxFees,
        tax,
        mealTax,
        processingFee,
        convenienceFee,
        deliveryFee,
        surcharge,
        tip,
        promoDiscount,
        total,
        cashPayment,
        isLoading: isCartLoading,
        deliveryDriverTip,
        externalDeliveryDriverTip,
        externalDeliveryFee,
        externalDeliveryTax,
    } = useCart();

    const {tax_rate,meal_tax_rate,theme} = restaurant;

    const isMenuPath = HOME_ROUTE === window.location.pathname;
    const isCheckoutPath = window.location.pathname.startsWith(CHECKOUT_ROUTE);
    const isGiftPath = window.location.pathname.startsWith(CHECKOUT_GIFT_ROUTE);
    // The exact contact-info checkout step only (not /checkout/card, /checkout/summary, etc.).
    const isCheckoutInfoPath = window.location.pathname === CHECKOUT_ROUTE
        || window.location.pathname === `${CHECKOUT_ROUTE}/`;
    // If this checkout step has its own primary "Next" CTA, mirror it from the side cart by
    // forwarding the click, so validation/advance logic stays in one place (on the page).
    const pageNextCtaId = isGiftPath ? 'giftNext' : (isCheckoutInfoPath ? 'checkoutNext' : null);
    const isEditableItems = ! window.location.pathname.startsWith(CHECKOUT_ROUTE);
    
    const handlerResetCart = (e) => {
        e.preventDefault();
        dispatch(resetCart());
    };

    const handlerShowCartItem = (e, itemIndex) => {
        e.preventDefault();

        dispatch(showCartItem(itemIndex));
    };

    const handlerRemoveCartItem = (e, itemIndex) => {
        e.preventDefault();
        dispatch(removeItemFromCart(itemIndex, restaurant));
    };

    const handlerGoToPage = (e, url, pageName) => {
        e.preventDefault();

        dispatch(hideSidebar());
        dispatch(showPage(pageName));
        history.push(url);
    };

    const handlerOpenCheckout = (e) => {
        const list = categories.filter(category => category.minimum_items !== 0 || category.maximum_items !== 0);
        const categoriesQty = {};

        list.forEach(category => {
            categoriesQty[category.id] = categoriesQty[category.id] || 0;

            const itemIds = category.items.map(item => item.id);
            categoriesQty[category.id] += cart.reduce((acc, cur) => acc + (itemIds.includes(cur.id) ? cur.quantity : 0), 0);
        });

        const categoriesIds = Object.keys(categoriesQty).map(id => +id);
        const cartCategories = list.filter(category => categoriesIds.includes(category.id) && categoriesQty[category.id] > 0);
        
        const categoryRequired = cartCategories.find(category => {
            const totalQuantity = categoriesQty[category.id];
            if (category.minimum_items > totalQuantity) {
                return true;
            }

            if (category.maximum_items !== 0 && category.maximum_items < totalQuantity) {
                return true;
            }

            return false;
        });
        
        if (categoryRequired) {
            const totalQuantity = categoriesQty[categoryRequired.id];
            
            if (categoryRequired.minimum_items > totalQuantity) {
                dispatch(showMenuAlert(`Please select ${categoryRequired.minimum_items - totalQuantity} more items from category ${categoryRequired.name}`));
            }

            if (categoryRequired.maximum_items !== 0 && categoryRequired.maximum_items < totalQuantity) {
                dispatch(showMenuAlert(`Please remove ${totalQuantity - categoryRequired.maximum_items} items from category ${categoryRequired.name}`));
            }
            
            return false;
        }

        let checkoutValue = restaurant.minimum_checkout_value;
        if (orderMethod === orderMethods.pickup && paymentMethod === paymentMethods.cash) {
            checkoutValue = restaurant.minimum_checkout_cash_value;
        }
        else if ([orderMethods.delivery, orderMethods.uber_delivery].includes(orderMethod) && paymentMethod === paymentMethods.card) {
            checkoutValue = restaurant.minimum_delivery_checkout_value;
        }
        else if ([orderMethods.delivery, orderMethods.uber_delivery].includes(orderMethod) && paymentMethod === paymentMethods.cash) {
            checkoutValue = restaurant.minimum_delivery_checkout_cash_value;
        }

        if (checkoutValue > 0 && checkoutValue > total) {
            dispatch(showMinimumCheckoutAlert());
            return false;
        }
        
        handlerGoToPage(e, CHECKOUT_ROUTE, pages.checkout);
    };
    
    const handlerPlaceGroupOrder = async () => {
        setIsLoading(true);

        const group_id = getSessionSettingsStorage('group_id');
        const action = getSessionSettingsStorage('action');

        try {
            const {data: response} = await axiosClient.post(
                `/api/restaurant/${window.vendor}/group-order`,
                {
                    cart,
                    group_id,
                    do: action
                }
            );

            dispatch(resetCart());
            
            window.location.replace(response.redirect_url);
        }
        catch (e) {
            const {response: {data}} = e;
            window.location.replace(HOME_ROUTE);
        }
    };

    const handleCloseItemDialog = useCallback(() => dispatch(hideCartItem()), [dispatch]);
    const handleConfirmItemDialog = useCallback((item) => dispatch(updateCartItem(item, restaurant)), [dispatch, restaurant]);
    
    useEffect(() => {
        if (!restaurant) {
            return;
        }

        const action = getSessionSettingsStorage('action');
        const group_id = getSessionSettingsStorage('group_id');
        const group_type = getSessionSettingsStorage('group_type');
        if (group_id && 'group' === group_type && 'submit' !== action) {
            setIsGroup(true);
        }
    });
    
    if (!restaurant) {
        return null;
    }

    if (cart.length === 0) {
        return (
            <SideCartEmpty />
        );
    }

    return (
        <div id="sideCart" className={classNames('col sidebar as-shadow cart-sidebar d-none d-xl-block', {show: activeSidebar === sidebars.cart})}>
            <SelectMenuItemDialog
                confirmLabel="Update"
                item={selectedItem}
                onClose={handleCloseItemDialog}
                onConfirm={handleConfirmItemDialog}
            />

            <MenuAlert />

            <div className="cart-details">
                {/*<h2 className="title">{restaurant_name}</h2>*/}
                <div className="cart-items">
                    <div className="cart-items-header">
                        <div>Qty</div>
                        <div>Item</div>
                        <div>Amount</div>
                    </div>
                    <div className="cart-items-list">
                        {cart && cart.map((item, index) => (
                            <div className="cart-item flex-column" key={`c_${index}_${item.id}`}>
                                <div className="d-flex pr-0">
                                    <div className="item-qty">{item.quantity}</div>
                                    <div className="item-desc">
                                        {item.name}&nbsp;&nbsp;
                                        {/*<a href="#" className="item-edit" onClick={(e) => handlerShowCartItem(e, index)}>*/}
                                        {/*    <i className="fas fa-edit"></i>*/}
                                        {/*</a>*/}

                                        {Object.entries(item.selectedOptions)
                                            .map(([key, label]) =>
                                                label.option_labels.map((o) => (
                                                    <span>{o.name}</span>
                                                ))
                                            )
                                        }

                                        <span>
                                            <em>{item.specialInstructions}</em>
                                        </span>
                                    </div>
                                    {/*<div className="item-remove mr-2">*/}
                                    {/*    <a href="#" onClick={(e) => handlerRemoveCartItem(e, index)}>*/}
                                    {/*        <i className="fas fa-minus-circle"></i>*/}
                                    {/*    </a>*/}
                                    {/*</div>*/}
                                    {item.offerDiscount === 0 && (
                                        <div className="item-amount pr-0">${(
                                            (
                                                item.price
                                                + Object
                                                    .entries(item.selectedOptions)
                                                    .map(([key, o]) => o.option_labels.reduce((acc, l) => acc + (l.quantity * l.price), 0))
                                                    .reduce((acc, p) => acc + p, 0)
                                            ) * item.quantity).toFixed(2)}</div>
                                    )}
                                    {item.offerDiscount > 0 && (
                                        <div className="item-amount pr-0">${(
                                            (
                                                item.price
                                                + Object
                                                    .entries(item.selectedOptions)
                                                    .map(([key, o]) => o.option_labels.reduce((acc, l) => acc + (l.quantity * l.price), 0))
                                                    .reduce((acc, p) => acc + p, 0)
                                            ) * item.quantity - item.offerDiscount).toFixed(2)}</div>
                                    )}
                                </div>
                                {isEditableItems && (
                                    <div className="item-buttons d-flex">
                                        <a href="#" className="item-edit mr-2" onClick={(e) => handlerShowCartItem(e, index)}>
                                            Edit
                                        </a>
                                        <a href="#" className="item-remove" onClick={(e) => handlerRemoveCartItem(e, index)}>
                                            Remove
                                        </a>
                                    </div>
                                )}
                            </div>
                        ))}
                        {cartGifts.length > 0 && cartGifts.map((item, index) => (
                            <div className="cart-item flex-column" key={`cg_${index}_${item.id}`}>
                                <div className="d-flex pr-0">
                                    <div className="item-qty">{item.quantity}</div>
                                    <div className="item-desc">
                                        {item.name}&nbsp;&nbsp;
                                        {/*<a href="#" className="item-edit" onClick={(e) => handlerShowCartItem(e, index)}>*/}
                                        {/*    <i className="fas fa-edit"></i>*/}
                                        {/*</a>*/}

                                        {Object.entries(item.selectedOptions)
                                            .map(([key, label]) =>
                                                label.option_labels.map((o, oi) => (
                                                    <span key={`${key}_${o.id || oi}`}>
                                                        {o.name}
                                                        {o.price > 0 ? ` (+\$${(o.price * (o.quantity || 1)).toFixed(2)})` : ''}
                                                    </span>
                                                ))
                                            )
                                        }

                                        <span>
                                            <em>{item.specialInstructions}</em>
                                        </span>
                                    </div>
                                    {/*<div className="item-remove mr-2">*/}
                                    {/*    <a href="#" onClick={(e) => handlerRemoveCartItem(e, index)}>*/}
                                    {/*        <i className="fas fa-minus-circle"></i>*/}
                                    {/*    </a>*/}
                                    {/*</div>*/}
                                    <div className="item-amount pr-0 text-uppercase text-red">{item.gift_base_price > 0 ? `\$${(item.gift_base_price * item.quantity).toFixed(2)}` : 'Free'}</div>
                                </div>
                                <div className="item-buttons d-flex">
                                    {/*<a href="#" className="item-edit mr-2" onClick={(e) => handlerShowCartItem(e, index)}>*/}
                                    {/*    Edit*/}
                                    {/*</a>*/}
                                    {/*<a href="#" className="item-remove" onClick={(e) => handlerRemoveCartItem(e, index)}>*/}
                                    {/*    Remove*/}
                                    {/*</a>*/}
                                </div>
                            </div>
                        ))}
                    </div>
                    {/*<div className="cart-items-option">*/}
                    {/*    <a href="#" id="cartEmpty" onClick={handlerResetCart}>Empty Bag</a>*/}
                    {/*</div>*/}
                </div>
                <div className="cart-compute">
                    <div className="compute-item">
                        <div>Sub-Total</div>
                        <div className="compute-amount">
                            ${(subTotal).toFixed(2)}
                        </div>
                    </div>
                    {taxFees > 0 && (
                        <div className="compute-item">
                            <div>
                                Tax&nbsp;&&nbsp;Fees&nbsp;
                                <PopoverHelp>
                                    {tax_rate > 0 && (
                                        <div className="d-flex justify-content-between">
                                            <span className="mr-1">Sales tax:</span>
                                            <span>${tax.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {meal_tax_rate > 0 && (
                                        <div className="d-flex justify-content-between">
                                            <span className="mr-1">Sales meal tax:</span>
                                            <span>${mealTax.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {processingFee > 0 && (
                                        <div className="d-flex justify-content-between">
                                            <span className="mr-1">Processing fee:</span>
                                            <span>${processingFee.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {convenienceFee > 0 && (
                                        <div className="d-flex justify-content-between">
                                            <span className="mr-1">Convenience fee: </span>
                                            <span>${convenienceFee.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {deliveryFee > 0 && (
                                        <div className="d-flex justify-content-between">
                                            <span className="mr-1">Delivery Fee:</span>
                                            <span>${deliveryFee.toFixed(2)}</span>
                                        </div>
                                    )}

                                    {externalDeliveryFee > 0 && (
                                        <div className="d-flex justify-content-between">
                                            <span className="mr-1">Delivery Fee Estimate:</span>
                                            <span>${externalDeliveryFee.toFixed(2)}</span>
                                        </div>
                                    )}
                                    {externalDeliveryTax > 0 && (
                                        <div className="d-flex justify-content-between">
                                            <span className="mr-1">Delivery Tax:</span>
                                            <span>${externalDeliveryTax.toFixed(2)}</span>
                                        </div>
                                    )}
                                </PopoverHelp>
                            </div>
                            <div className="compute-amount">
                                ${(taxFees).toFixed(2)}
                            </div>
                        </div>
                    )}
                    {/*{tax_rate > 0 && (*/}
                    {/*    <div className="compute-item">*/}
                    {/*        <div>Sales tax</div>*/}
                    {/*        <div className="compute-amount">*/}
                    {/*            ${tax.toFixed(2)}*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*)}*/}
                    {/*{processingFee > 0 && (*/}
                    {/*    <div className="compute-item">*/}
                    {/*        <div>Processing fee <PopoverHelp content="This fee is charged by our online partner, FastOrderNow, to help cover costs associated with the convenience of ordering online."/></div>*/}
                    {/*        <div className="compute-amount">*/}
                    {/*            ${processingFee.toFixed(2)}*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*)}*/}
                    {surcharge > 0 && (
                        <div className="compute-item">
                            <div>COVID-19 Surcharge <PopoverHelp content="A temporary COVID-19 service charge is added to each check to offset the costs of printing single-use menus, disposable gloves, outdoor furniture and tents, additional sanitizing and cleaning supplies, bathroom UVC filters, hand-sanitizing station and maintenance, face shields for the dishwashers, bartender wage adjustment, additional linen inventory for cleaning and sanitizing."/></div>
                            <div className="compute-amount">
                                ${surcharge.toFixed(2)}
                            </div>
                        </div>
                    )}
                    {/*{convenienceFee > 0 && (*/}
                    {/*    <div className="compute-item">*/}
                    {/*        <div>Convenience fee</div>*/}
                    {/*        <div className="compute-amount">*/}
                    {/*            ${convenienceFee.toFixed(2)}*/}
                    {/*        </div>*/}
                    {/*    </div>*/}
                    {/*)}*/}
                    {/*{deliveryFee > 0 && (*/}
                    {/*    <div className="compute-item">*/}
                    {/*        <div>Delivery Fee</div>*/}
                    {/*        <div className="compute-amount">${(deliveryFee).toFixed(2)}</div>*/}
                    {/*    </div>*/}
                    {/*)}*/}
                    {tip > 0 && (
                        <div className="compute-item">
                            <div>Tip</div>
                            <div className="compute-amount">${(tip).toFixed(2)}</div>
                        </div>
                    )}
                    {deliveryDriverTip > 0 && (
                        <div className="compute-item">
                            <div>Driver Tip</div>
                            <div className="compute-amount">${(deliveryDriverTip).toFixed(2)}</div>
                        </div>
                    )}
                    {externalDeliveryDriverTip > 0 && (
                        <div className="compute-item">
                            <div>External Driver Tip</div>
                            <div className="compute-amount">${(externalDeliveryDriverTip).toFixed(2)}</div>
                        </div>
                    )}
                    {promoDiscount > 0 && (
                        <div className="compute-item">
                            <div>Promo Discount</div>
                            <div className="compute-amount">-${(promoDiscount).toFixed(2)}</div>
                        </div>
                    )}
                    <div className="compute-item total">
                        <div>Total</div>
                        <div className="compute-amount">${(total).toFixed(2)}</div>
                    </div>
                </div>

                {isMenuPath && (
                    <div className="pb-5">
                        {!isGroup && (
                            <a id="goCheckout"
                               className="btn btn-block btn-primary cart-btn btn-payment"
                               href="#"
                               onClick={handlerOpenCheckout}
                               style={{
                                   color: theme.checkout_button_font_color,
                                   background: theme.checkout_button_background_color,
                                   borderColor: theme.checkout_button_background_color,
                               }}
                            >
                                Go to Checkout
                            </a>
                        )}

                        {isGroup && (
                            <button type="button"
                                    className="btn btn-primary btn-payment btn-block"
                                    onClick={handlerPlaceGroupOrder}
                                    disabled={cart.length === 0 || isLoading}
                                    style={{
                                        color: theme.checkout_button_font_color,
                                        background: theme.checkout_button_background_color,
                                        borderColor: theme.checkout_button_background_color,
                                    }}
                            >
                                {!isLoading && 'Add To Group Order'}
                                {isLoading && (<LoaderButton/>)}
                            </button>
                        )}
                    </div>
                )}

                {isCheckoutPath && (
                    <div className="pb-5">
                        {pageNextCtaId ? (
                            // On steps that have their own primary CTA (gift step, contact-info step),
                            // mirror it: forward the click to the page's own (validated) "Next" button
                            // so advancing / order placement stays in one place.
                            <button type="button"
                                    id="sideCartNext"
                                    className="btn btn-block btn-primary btn-payment"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const pageNextBtn = document.getElementById(pageNextCtaId);
                                        if (pageNextBtn) { pageNextBtn.click(); }
                                    }}
                                    style={{
                                        color: theme.checkout_button_font_color,
                                        background: theme.checkout_button_background_color,
                                        borderColor: theme.checkout_button_background_color,
                                    }}
                            >
                                Next
                            </button>
                        ) : (
                            <Link to={HOME_ROUTE} className="btn btn-block btn-secondary btn-back-menu" id="backToMenu">
                                Back to the Menu
                            </Link>
                        )}
                    </div>
                )}
            </div>

        </div>
    );
}

export default withRouter(SideCart);